import React, {
	FC, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import {
	ColumnPicker, useUrlParams,
} from '@aleph/shared-components';
import {
	columnEditorPlugin,
	dashboardViewPlugin,
	elasticPropertyPlugin,
	nameSizingPlugin,
	SuperGridAnchors,
	useSuperGridEngine,
	useSuperGridPlugin,
	validatePlugin,
} from '@aleph/supergrid';
import { useApolloClient } from '@apollo/client';
import { REDDIT_SUPERGRID_CONFIG } from 'wb-reddit/app/routes/superGrid/config';
import {
	DEFAULT_REDDIT_COL_DEF, filterSortField,
	getRedditSuperGridRowClassRules,
	mapSortField,
} from 'wb-reddit/app/routes/superGrid/view/utils';
import LoadingCellRenderer from 'wb-reddit/app/routes/superGrid/cellRenderers/loading';
import RedditActionRow from 'wb-reddit/app/routes/superGrid/actionRow/container';
import { debounce, isEqual } from 'lodash';
import {
	MAPPED_COLUMN_NAMES,
	REDDIT_ALWAYS_VISIBLE_COLUMNS,
	REDDIT_SUPERGRID_ICONS,
	RedditBreakdowns,
	RedditLevel,
	RedditSuperGridContext,
	processValueFromJSON,
	processValueToJSON,
} from 'wb-reddit/app/routes/superGrid/utils';
import AddNewRedditEntityButton from 'wb-reddit/app/routes/superGrid/mutator/add/button';
import NoRowsOverlay from 'wb-reddit/app/routes/superGrid/view/noRowsOverlay';
import RedditSuperGridBreakdownSelector from 'wb-reddit/app/routes/superGrid/tool/breakdownSelector';
import AdvertiserOAuth from 'wb-reddit/app/routes/superGrid/tool/advertiserOAuth/container';
import useRedditEditPlugin from 'wb-reddit/app/routes/superGrid/view/hooks/useRedditEditPlugin';
import { REDDIT_CATEGORIZED_COLUMNS } from 'wb-reddit/app/routes/utils';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import {
	GridApi,
	ColumnApi,
	GridReadyEvent,
	RowSelectedEvent,
	SortChangedEvent,
	RangeSelectionChangedEvent,
	FirstDataRenderedEvent,
	CellValueChangedEvent,
	CellClickedEvent,
	BodyScrollEvent,
} from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import useInstantEditPlugin from 'wb-reddit/app/routes/superGrid/hooks/useInstantEditPlugin';
import RedditSupergridBulkActions from 'wb-reddit/app/routes/superGrid/actionRow/bulkActions/container';
import { useNavigate, useParams } from 'react-router-dom';
import { ValidationNavigator } from 'wb-spotify/app/common/supergridValidation';

LicenseManager.setLicenseKey(
	// eslint-disable-next-line max-len
	'Using_this_AG_Grid_Enterprise_key_( AG-043155 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Httpool d.o.o. )_is_granted_a_( Multiple Applications )_Developer_License_for_( 3 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 1 July 2024 )____[v2]_MTcxOTc4ODQwMDAwMA==3f4d73dbd0b55114fae1030bca4daf44',
);

const RedditViewSuperGrid: FC = () => {
	const redditSuperGridContext = useContext(RedditSuperGridContext);
	const [selectedMetrics, setSelectedMetrics] = useState<string[]>(REDDIT_CATEGORIZED_COLUMNS[0].metrics.map(metric => metric.value));
	const [api, setApi] = useState<GridApi | null>(null);
	const [columnApi, setColumnApi] = useState<ColumnApi | null>(null);
	const navigate = useNavigate();

	const { params, setUrlParam } = useUrlParams({ navigate, useParams });
	const platformUser = useUserMetaData();

	const onGridReady = (e: GridReadyEvent) => {
		setApi(e.api);
		setColumnApi(e.columnApi);
		redditSuperGridContext.setGridApi(e.api);
	};

	const client = useApolloClient();
	const gridBodyElement = document.querySelector('#super-grid .ag-center-cols-viewport');

	const [
		selectedBreakdown,
		setSelectedBreakdownType,
	] = useState<RedditBreakdowns | undefined>(RedditBreakdowns.ENTITIES);

	const hiddenLevels = useMemo(() => {
		if (selectedBreakdown === RedditBreakdowns.ENTITIES) {
			return [RedditLevel.BREAKDOWN];
		}

		return Object.values(RedditLevel).filter(
			level => ![params?.level, RedditLevel.BREAKDOWN].includes(level),
		);
	}, [selectedBreakdown, params?.level]);

	const getVariables = useMemo(() => (useBreakdown: boolean) => (level: RedditLevel) => {
		const filtersFinal = { ...params.filters ?? {} };

		if (level !== RedditLevel.AD_ACCOUNT) {
			delete filtersFinal.agencyAccountId;
			delete filtersFinal.userGroupId;
			delete filtersFinal.userId;
		}

		return {
			orderBy: [
				...(
					level === RedditLevel.AD_ACCOUNT ? [{
						column:    'clientPinned',
						direction: 'DESC',
					}] : []
				),
				...params.sort?.filter(sortField => filterSortField(mapSortField(sortField.colId, level) as string, level))?.map(sortEntity => ({
					column:    mapSortField(sortEntity.colId, level),
					direction: sortEntity.sort?.toUpperCase(),
				})) ?? {},
			],
			filters: {
				...filtersFinal,
				...(useBreakdown ? { date: { value: params.date } } : {}),
			},
			statsFilter: { date: { value: params.date } },
		};
	}, [
		JSON.stringify(params?.filters ?? {}),
		JSON.stringify(params?.date ?? {}),
		JSON.stringify(params?.sort ?? {}),
	]);

	const debouncedRefresh = useMemo(() => debounce(() => api?.refreshCells({ force: true }), 100), [api]);

	const callbacks = useMemo(() => ({
		beforeSuccess: (beforeSuccessParams) => {
			if (
				beforeSuccessParams.items?.length === 0
				&& !beforeSuccessParams.wasCreateAdded
				&& (api?.getRenderedNodes()?.filter(node => !!node?.data)?.length ?? 0) === 0
			) {
				api?.showNoRowsOverlay();
			} else {
				api?.hideOverlay();
			}
		},
	}), [api, selectedBreakdown, JSON.stringify(params?.date ?? {})]);

	const resolverContext = useMemo(() => (
		{
			breakdown: selectedBreakdown,
			client,
			getVariables,
		}
	), [selectedBreakdown, client, getVariables]);

	const {
		props: {
			context: superGridContext,
			onSortChanged: supergridOnSortChanged,
			onBodyScroll,
			...supergridProps
		},
		deepestVisibleLevel,
		updateDeepestVisibleLevel,
		navigateToColumn,
		setElasticProperty,
	} = useSuperGridEngine({
		callbacks,
		gridBodyElement,
		gridApi:      api,
		gridTopLevel: params?.level ?? RedditLevel.AD_ACCOUNT,
		config:       REDDIT_SUPERGRID_CONFIG,
		plugins:      [
			columnEditorPlugin,
			useSuperGridPlugin(dashboardViewPlugin, {
				configPerRowLevel: [{ level: RedditLevel.AD_ACCOUNT }],
			}),
			validatePlugin,
			useRedditEditPlugin,
			elasticPropertyPlugin,
			useSuperGridPlugin(nameSizingPlugin, {
				lastNameColId: 'breakdownName',
			}),
			useInstantEditPlugin,
		],
		hiddenLevels,
		context:        resolverContext,
		visibleColumns: {
			GLOBAL: [
				...REDDIT_ALWAYS_VISIBLE_COLUMNS,
				...selectedMetrics,
				selectedBreakdown === RedditBreakdowns.ENTITIES ? 'settings' : '',
			],
		},
	});

	useEffect(() => {
		setElasticProperty(undefined);
	}, [selectedBreakdown]);

	const superGridRowClassRules = useMemo(
		() => getRedditSuperGridRowClassRules(),
		[],
	);

	useEffect(() => {
		if (api) {
			api?.refreshHeader();
			api?.deselectAll();
			updateDeepestVisibleLevel(api);
		}
	}, [params?.level]);

	useEffect(() => {
		columnApi?.applyColumnState({
			state:        params?.sort,
			defaultState: { sort: null },
		});
	}, [params?.sort]);

	const onRowSelectionChanged = (e: RowSelectedEvent) => {
		e.api.refreshCells({ force: true });
	};

	const onColumnResized = () => {
		debouncedRefresh();
	};

	const onSortChanged = (e: SortChangedEvent) => {
		supergridOnSortChanged?.(e);

		const gridSort = e.columnApi.getColumnState().filter(col => !!col.sort).map((col) => ({
			colId: col.colId,
			sort:  col.sort,
		}));

		e.api?.refreshHeader();

		if (!isEqual(gridSort, params.sort)) {
			setUrlParam(
				'sort',
				gridSort,
			);
		}
	};

	const onRangeSelectionChanged = (e: RangeSelectionChangedEvent) => {
		e.api.refreshCells({ force: true });
	};

	const onFirstDataRendered = (e: FirstDataRenderedEvent) => {
		if (params?.goToColumn?.col) {
			setElasticProperty({
				levels: [params?.level as RedditLevel],
				name:   'settingsElastic',
				field:  'settings',
			});

			setTimeout(() => {
				navigateToColumn(params?.goToColumn?.col);
				e.api?.addCellRange({
					rowStartIndex: 0,
					rowEndIndex:   0,
					columnStart:   params?.goToColumn?.col,
					columnEnd:     params?.goToColumn?.col,
				});

				if (params?.goToColumn?.newValue) {
					setTimeout(() => {
						api?.getDisplayedRowAtIndex(0)?.setDataValue(
							params?.goToColumn?.col,
							params?.goToColumn?.newValue,
						);
						api?.flashCells({
							columns:    [params?.goToColumn?.col],
							flashDelay: 200,
						});
						setTimeout(() => {
							setUrlParam('goToColumn', undefined);
						});
					}, 1000);
				} else {
					setUrlParam('goToColumn', undefined);
				}
			}, 500);
		}
	};

	const onCellValueChanged = (e: CellValueChangedEvent) => {
		supergridProps?.onCellValueChanged?.(e);
	};

	const gridWidth = document.getElementById('reddit-super-grid-anchors')?.clientWidth ?? 0;
	// this is the offset of the breakdown button from the right side of the grid
	const breakdownButtonOffset = 110;

	const isServerSideGroupOpenByDefault = (openParams) => {
		const shouldExpand = (params.autoExpand ?? []).includes(openParams?.data?.id);

		if (shouldExpand) {
			setUrlParam(
				'autoExpand',
				params.autoExpand?.filter(id => id !== openParams?.data?.id),
			);
		}

		return shouldExpand
				&& openParams.data?.level !== RedditLevel.AD
				&& !openParams.data?.isCreate;
	};

	const columnDefs = useMemo(() => {
		if (platformUser?.readOnly) {
			return (supergridProps?.columnDefs ?? []).map(colDef => ({
				...colDef,
				editable: false,
			}));
		}

		return supergridProps.columnDefs;
	}, [supergridProps.columnDefs]);

	const onCellClicked = (e: CellClickedEvent) => {
		if (e.data?.level !== RedditLevel.AD_ACCOUNT) {
			supergridProps?.onCellClicked?.(e);
		}
	};

	const onScroll = useCallback((e: BodyScrollEvent) => {
		const columnEditor = document.getElementById('column-editor-wrapper');

		if (!columnEditor) {
			api?.stopEditing(true);
		}
		onBodyScroll?.(e);
	}, [onBodyScroll]);

	return (
		<div className="flex-1 flex flex-col min-w-0">
			<ValidationNavigator
				gridApi={api}
				mappedFields={MAPPED_COLUMN_NAMES}
			/>

			<RedditActionRow gridApi={api} />

			<div className="relative">
				<RedditSupergridBulkActions gridApi={api} />

				<div className="flex justify-between items-center gap-[8px]">
					<div
						data-testid="sub-anchor"
						id="reddit-super-grid-anchors"
						className="relative flex-1 whitespace-nowrap overflow-hidden"
					>
						<SuperGridAnchors
							gridTopLevel={params?.level ?? RedditLevel.AD_ACCOUNT}
							gridBodyElement={gridBodyElement}
							gridWidth={gridWidth - breakdownButtonOffset}
							config={REDDIT_SUPERGRID_CONFIG}
							minWidth={36}
							gridApi={api}
							deepestVisibleLevel={deepestVisibleLevel}
							leftBoundary="expand"
							pinnedRightWidth={50}
							pinLeft={true}
						>
							{!platformUser?.readOnly && <div className="absolute h-full pl-[1px] flex items-center">
								{params.level !== RedditLevel.AD_ACCOUNT
									? <AddNewRedditEntityButton />
									: <AdvertiserOAuth />}
							</div>}

						</SuperGridAnchors>
					</div>

					<RedditSuperGridBreakdownSelector
						selectedBreakdown={selectedBreakdown}
						setSelectedBreakdownType={setSelectedBreakdownType}
					/>

					<ColumnPicker
						onConfirm={setSelectedMetrics}
						selectedMetrics={selectedMetrics}
						metricCategories={REDDIT_CATEGORIZED_COLUMNS}
					/>
				</div>
			</div>

			<div
				id="super-grid"
				className="ag-theme-alpine flex-1 mr-[-40px] mb-[-11px]"
			>
				<AgGridReact
					{...supergridProps}
					blockLoadDebounceMillis={100}
					columnDefs={columnDefs}
					// key={`${params?.level}_${JSON.stringify(params?.filters)}_${selectedBreakdown}`}
					processCellFromClipboard={processValueFromJSON}
					processCellForClipboard={processValueToJSON}
					onFirstDataRendered={onFirstDataRendered}
					animateRows
					autoSizePadding={0}
					rowModelType="serverSide"
					onGridReady={onGridReady}
					onBodyScroll={onScroll}
					maxConcurrentDatasourceRequests={1}
					rowSelection="multiple"
					rowBuffer={Number(localStorage.getItem('debug_rowBuffer') ?? 10)}
					cacheBlockSize={Number(localStorage.getItem('debug_cacheBlockSize') ?? 100)}
					headerHeight={36}
					rowHeight={144}
					suppressContextMenu
					enableRangeSelection
					enableFillHandle
					suppressRowClickSelection
					enableGroupEdit
					suppressPropertyNamesCheck
					enableRangeHandle
					serverSideSortAllLevels
					purgeClosedRowNodes
					isServerSideGroupOpenByDefault={isServerSideGroupOpenByDefault}
					noRowsOverlayComponent={NoRowsOverlay}
					onSortChanged={onSortChanged}
					onColumnResized={onColumnResized}
					onCellValueChanged={onCellValueChanged}
					onSelectionChanged={onRowSelectionChanged}
					onRangeSelectionChanged={onRangeSelectionChanged}
					defaultColDef={DEFAULT_REDDIT_COL_DEF}
					loadingCellRenderer={LoadingCellRenderer}
					undoRedoCellEditing
					onCellClicked={onCellClicked}
					rowClassRules={superGridRowClassRules}
					icons={REDDIT_SUPERGRID_ICONS}
					context={{
						...superGridContext,
						breakdown: selectedBreakdown,
					}}
					getRowId={(rowParams) => `${rowParams.data?.id}_${rowParams.data?.level}`}
					onModelUpdated={e => {
						updateDeepestVisibleLevel(e.api);
						supergridProps?.onModelUpdated?.(e);
					}}
					suppressDragLeaveHidesColumns
				/>
			</div>
		</div>
	);
};

export default RedditViewSuperGrid;
